import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { MatchableItem, MatchItemType } from "./types";

/**
 * The drawer's match selection.
 *
 * `displacedTransferItems` holds transfer-pair items a document selection
 * pushed out. On cash basis a document match and a transfer pair are mutually
 * exclusive — approving the document unpairs the transfer server-side — but
 * the pair may already be persisted as `transaction.pairedTransferId`, in
 * which case only `DELETE /match-transfer` can undo it. Keeping the item aside
 * means removing the documents again restores its row, so the user can still
 * unmatch it for real instead of leaving the UI diverged from the server.
 */
interface MatchSelectionState {
    selectedItems: MatchableItem[];
    displacedTransferItems: MatchableItem[];
}

interface CreateSelectionStateParams {
    items: MatchableItem[];
    ledgerBasis: AccountingLedgerBasis;
}

interface AddItemToSelectionParams {
    state: MatchSelectionState;
    item: MatchableItem;
    ledgerBasis: AccountingLedgerBasis;
}

interface RemoveItemFromSelectionParams {
    state: MatchSelectionState;
    itemId: string;
}

interface EnrichSelectionStateParams {
    state: MatchSelectionState;
    itemsById: ReadonlyMap<string, MatchableItem>;
}

function isTransfer(item: MatchableItem): boolean {
    return item.type === MatchItemType.TRANSACTION;
}

function hasDocument(items: MatchableItem[]): boolean {
    return items.some((item) => !isTransfer(item));
}

/**
 * Seeds the selection from the drawer's preselected items (existing matches,
 * suggestions and the pending transfer pair), applying the same cash-basis
 * invariant `addItemToSelection` enforces so the drawer can never open in a
 * state the helper forbids.
 */
export function createSelectionState({
    items,
    ledgerBasis,
}: CreateSelectionStateParams): MatchSelectionState {
    if (ledgerBasis !== AccountingLedgerBasis.cash || !hasDocument(items)) {
        return { selectedItems: items, displacedTransferItems: [] };
    }

    return {
        selectedItems: items.filter((item) => !isTransfer(item)),
        displacedTransferItems: items.filter(isTransfer),
    };
}

/**
 * Cash-basis transfer matching is strictly a single equal-and-opposite pair,
 * so picking a transaction replaces the whole selection. Invoice/bill matches
 * accumulate like accrual matching, but cannot coexist with a transfer pair.
 */
export function addItemToSelection({
    state,
    item,
    ledgerBasis,
}: AddItemToSelectionParams): MatchSelectionState {
    if (state.selectedItems.some((selected) => selected.id === item.id)) {
        return state;
    }

    if (ledgerBasis !== AccountingLedgerBasis.cash) {
        return {
            selectedItems: [...state.selectedItems, item],
            displacedTransferItems: state.displacedTransferItems,
        };
    }

    if (isTransfer(item)) {
        // Cash-basis transfer matching is a single pair, so the chosen item
        // replaces the selection — but a *persisted* pair kept aside stays
        // aside, since dropping it would make the only row that can reach
        // `DELETE /match-transfer` unreachable for the rest of the session.
        return {
            selectedItems: [item],
            displacedTransferItems: state.displacedTransferItems.filter(
                (displaced) => displaced.id !== item.id,
            ),
        };
    }

    return {
        selectedItems: [
            ...state.selectedItems.filter((selected) => !isTransfer(selected)),
            item,
        ],
        displacedTransferItems: [
            ...state.displacedTransferItems,
            ...state.selectedItems.filter(isTransfer),
        ],
    };
}

/**
 * Removing the last document restores the transfer pair the documents
 * displaced, so it becomes unmatchable again.
 */
export function removeItemFromSelection({
    state,
    itemId,
}: RemoveItemFromSelectionParams): MatchSelectionState {
    const selectedItems = state.selectedItems.filter(
        (item) => item.id !== itemId,
    );

    if (
        state.displacedTransferItems.length === 0 ||
        hasDocument(selectedItems)
    ) {
        return { ...state, selectedItems };
    }

    return {
        selectedItems: [...selectedItems, ...state.displacedTransferItems],
        displacedTransferItems: [],
    };
}

/**
 * Replaces already-tracked items with their fully loaded counterparts as the
 * drawer's enrichment queries resolve, without changing the selection itself.
 */
export function enrichSelectionState({
    state,
    itemsById,
}: EnrichSelectionStateParams): MatchSelectionState {
    return {
        selectedItems: state.selectedItems.map(
            (item) => itemsById.get(item.id) ?? item,
        ),
        displacedTransferItems: state.displacedTransferItems.map(
            (item) => itemsById.get(item.id) ?? item,
        ),
    };
}
